import { Checkbox, Form, Modal, Switch, Table } from "antd"
import { Cards } from "../../../components/cards/frame/cards-frame"
import { InfoCircleOutlined } from '@ant-design/icons';
import { UserTableStyleWrapper } from "../style";
import { TableWrapper } from "../../styled";
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../../components/buttons/buttons";
import { Link, useParams } from "react-router-dom";
import HttpStatus from "../../../utility/status.enum";
import { useDispatch } from "react-redux";
import { getConfigurationDetails, updateConfiguration } from "../../../redux/configurations/actionCreator";
import { fetchHostListing } from '../../../redux/manageSettings/actionCreator';
import Spinner from "../../common/Spinner";
import Functions from '../../../utility/functions';
import Notification from '../../../utility/notification.js';
import Heading from '../../../components/heading/heading.js';

const Configurations = () => {
    const { perPageRecord, backEndPrefix } = global;
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const hostSlug = useParams()?.hostSlug ?? '';
    const [configurationList, setConfigurationList] = useState([]);
    const [spinnerLoader, setSpinnerLoader] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: perPageRecord,
        totalItems: 0,
    });
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState('');

    const [listingLists, setListingLists] = useState([]);
    const [sortedListingLists, setSortedListingLists] = useState([]);
    const [selectedListings, setSelectedListings] = useState({});
    const [isListingSelectionPopupOpen, setIsListingSelectionPopupOpen] = useState(false);
    const [currentFeatureSlug, setCurrentFeatureSlug] = useState(null);
    const [preListingData, setPreListingData] = useState({});

    const [isEditMode, setIsEditMode] = useState(false);
    const [editBackup, setEditBackup] = useState({ configurationList: [], selectedListings: {} });

    const handleToggleChange = (checkedValue, record, field) => {
        const getChannelName = (f) => {
            if (f === 'is_airbnb_enable') return 'Airbnb';
            if (f === 'is_vrbo_enable') return 'Vrbo';
            if (f === 'is_other_enable') return 'Other';
            return 'Channel';
        };

        const idVerificationRow = configurationList.find(item => item.feature_slug === 'veriff_verification');
        const offenderRow = configurationList.find(item => item.feature_slug === 'sex_offender');
        const channelName = getChannelName(field);

        // Offender Check can be turned ON only if ID Verification is ON for the same channel
        if (record.feature_slug === 'sex_offender' && checkedValue) {
            if (idVerificationRow?.[field] !== 1) {
                Notification.addNotificationError(
                    `To enable Offender Check, please turn ON ID Verification for ${channelName}`
                );
                return;
            }
        }

        // ID Verification cannot be turned OFF if Offender Check is ON for the same channel
        if (record.feature_slug === 'veriff_verification' && !checkedValue) {
            if (offenderRow?.[field] === 1) {
                Notification.addNotificationError(
                    `Cannot turn OFF ID Verification because Offender Check is ON for ${channelName}`
                );
                return;
            }
        }

        const updatedList = configurationList.map((item) => {
            if (item.feature_slug === record.feature_slug) {
                return { ...item, [field]: checkedValue ? 1 : 2 };
            }
            return item;
        });
        setConfigurationList(updatedList);
    };

    const handleAllToggleChange = (checked, record) => {
        const getChannelName = (f) => {
            if (f === 'is_airbnb_enable') return 'Airbnb';
            if (f === 'is_vrbo_enable') return 'Vrbo';
            if (f === 'is_other_enable') return 'Other';
            return 'Channel';
        };

        const idVerificationRow = configurationList.find(item => item.feature_slug === 'veriff_verification');
        const offenderRow = configurationList.find(item => item.feature_slug === 'sex_offender');
        const fields = ['is_airbnb_enable', 'is_vrbo_enable', 'is_other_enable'];

        // Offender Check All ON — ID Verification must be ON for every channel
        if (record.feature_slug === 'sex_offender' && checked) {
            const offChannels = fields.filter(f => idVerificationRow?.[f] !== 1).map(getChannelName);
            if (offChannels.length) {
                Notification.addNotificationError(
                    `To enable Offender Check, please turn ON ID Verification for ${offChannels.join(', ')}`
                );
                return;
            }
        }

        // ID Verification All OFF — Offender Check must not be ON for any channel
        if (record.feature_slug === 'veriff_verification' && !checked) {
            const onChannels = fields.filter(f => offenderRow?.[f] === 1).map(getChannelName);
            if (onChannels.length) {
                Notification.addNotificationError(
                    `Cannot turn OFF ID Verification because Offender Check is ON for ${onChannels.join(', ')}`
                );
                return;
            }
        }

        const updatedList = configurationList.map((item) => {
            if (item.feature_slug === record.feature_slug) {
                return {
                    ...item,
                    is_airbnb_enable: checked ? 1 : 2,
                    is_vrbo_enable: checked ? 1 : 2,
                    is_other_enable: checked ? 1 : 2,
                };
            }
            return item;
        });
        setConfigurationList(updatedList);
    };

    //  Handles info icon click (Intercom OR Modal fallback)
    const handleInfoClick = (e, record) => {
        e?.preventDefault();

        const description = record?.description || '';

        //  Check if description contains Intercom article ID
        const hasIntercomArticleId = Functions.findIntercomArticleId(description);

        //  If Intercom ID exists → let Intercom handle it
        //  Else -> fallback to modal
        if (!hasIntercomArticleId) {
            setSelectedDescription(description);
            setIsInfoModalOpen(true);
        }
    };

    const handleEnterEditMode = () => {
        setEditBackup({
            configurationList: structuredClone(configurationList),
            selectedListings: structuredClone(selectedListings),
        });
        setIsEditMode(true);
    };

    const handleCancelEdit = () => {
        setConfigurationList(editBackup.configurationList);
        setSelectedListings(editBackup.selectedListings);
        setIsEditMode(false);
    };

    const fetchHostListings = async () => {
        try {
            const formData = { host_slug: hostSlug };
            const listingResponse = await dispatch(fetchHostListing(formData));
            if (listingResponse?.status === HttpStatus.HTTP_OK) {
                const listingData = listingResponse?.result || [];
                setListingLists(listingData);
                setSortedListingLists(listingData);
            }
        } catch (error) {}
    };

    const openListingSelectionPopup = (featureSlug) => {
        if (!listingLists || listingLists.length === 0) return;

        // listings: [] from API means all listings are selected
        let currentSelected = selectedListings[featureSlug] ?? [];
        if (currentSelected.length === 0) {
            currentSelected = listingLists.map(item => item.slug);
            setSelectedListings(prev => ({ ...prev, [featureSlug]: currentSelected }));
        }

        const selectedSlugs = new Set(currentSelected);
        const selected = [];
        const notSelected = [];
        for (const item of listingLists) {
            (selectedSlugs.has(item.slug) ? selected : notSelected).push(item);
        }
        setSortedListingLists([...selected, ...notSelected]);
        setCurrentFeatureSlug(featureSlug);
        setIsListingSelectionPopupOpen(true);
    };

    const closeListingSelectionPopup = (isCancelled = false) => {
        setIsListingSelectionPopupOpen(false);
        if (isCancelled && preListingData?.selectedListing) {
            setSelectedListings(preListingData.selectedListing);
        }
    };

    // A feature is "active" when it is enabled for at least one channel
    const isFeatureChannelActive = (featureSlug) => {
        const row = configurationList.find(item => item.feature_slug === featureSlug);
        return (
            row?.is_airbnb_enable === 1 ||
            row?.is_vrbo_enable === 1 ||
            row?.is_other_enable === 1
        );
    };

    // empty array means all listings are selected for that feature
    const getEffectiveListings = (featureSlug) => {
        const listings = selectedListings[featureSlug] ?? [];
        return listings.length === 0 ? listingLists.map(l => l.slug) : listings;
    };

    const handleListingCheckboxChange = (event, listingSlug) => {
        const { checked } = event.target;

        // A listing can only be in Offender Check if it is also in ID Verification, so:
        // - can't remove a listing from ID Verification while it's still in Offender Check
        // - can't add a listing to Offender Check unless it's already in ID Verification
        if (!checked && currentFeatureSlug === 'veriff_verification' && isFeatureChannelActive('sex_offender')) {
            if (getEffectiveListings('sex_offender').includes(listingSlug)) {
                Notification.addNotificationError(
                    `You can't remove this listing from ID Verification until you remove it from Offender Check first.`
                );
                return;
            }
        }
        if (checked && currentFeatureSlug === 'sex_offender' && isFeatureChannelActive('veriff_verification')) {
            if (!getEffectiveListings('veriff_verification').includes(listingSlug)) {
                Notification.addNotificationError(
                    `You can't add this listing to Offender Check until you add it to ID Verification first.`
                );
                return;
            }
        }

        setSelectedListings((prevSelected) => {
            const prevGroupList = prevSelected[currentFeatureSlug] || [];
            const updatedGroupList = checked
                ? [...new Set([...prevGroupList, listingSlug])]
                : prevGroupList.filter((slug) => slug !== listingSlug);
            return {
                ...prevSelected,
                [currentFeatureSlug]: updatedGroupList,
            };
        });
    };

    const handleListingSelection = () => {
        if (selectedListings[currentFeatureSlug]?.length) {
            closeListingSelectionPopup();
        } else {
            Notification.addNotificationError('Please select at least one listing');
        }
    };

    const listingTableColumns = [
        {
            title: 'Listing',
            dataIndex: 'listing_name',
            key: 'listing_name',
        },
    ];

    const listingTableData = sortedListingLists.map((listingData) => {
        const { slug } = listingData;
        const isChecked = selectedListings[currentFeatureSlug]?.includes(slug);
        return {
            key: slug,
            listing_name: (
                <div className="user-info">
                    <div className="user-info-checkbox">
                        <Form.Item className="mb-0" name="listing_slug" labelCol={{ className: 'custom-checkbox-label' }}>
                            <Checkbox
                                onChange={(e) => handleListingCheckboxChange(e, slug)}
                                checked={isChecked}
                                disabled={!isEditMode}
                                className="template-detail-view-checkbox"
                            />
                        </Form.Item>
                    </div>
                    <figure>
                        <img className="user-info-image" src={listingData?.listing_image} alt="" />
                    </figure>
                    <figcaption>
                        <Heading className="user-name" as="h6">
                            {global.checkPermission('listing-show') ? (
                                <Link target="_blank" to={`${backEndPrefix}/listing/detail/${slug}`}>
                                    <a className="boldurlLink">{listingData?.listing_name}</a>
                                </Link>
                            ) : (
                                <span>{listingData?.listing_name}</span>
                            )}
                        </Heading>
                        <span className="user-designation" />
                        <span>
                            <a href={listingData?.checkin_url_link} target="__blank" className="urlLink">
                                {listingData?.checkin_url}
                            </a>
                        </span>
                    </figcaption>
                </div>
            ),
        };
    });

    const cmsTableColumns = [
        {
            title: 'Feature',
            dataIndex: 'title',
            key: 'title',
            width: '35%',
            render: (text, record) => (
                <div className="feature-info-icon">
                    <span>{text}</span>
                    {/*  Show info icon ONLY if is_allowed_info === 1 */}
                    {record?.is_allowed_info === 1 && (
                        <span onClick={(e) => handleInfoClick(e, record)}>
                            <InfoCircleOutlined className='configuration-info-icon' />
                        </span>
                    )}
                </div>
            )
        },
        {
            title: 'All',
            dataIndex: 'all',
            key: 'all',
            width: '10%',
            render: (_, record) => {

                //  Collect all channel values
                const values = [
                    record.is_airbnb_enable,
                    record.is_vrbo_enable,
                    record.is_other_enable
                ];

                //  Determine toggle state
                const allOn = values.every(v => v === 1);   // All enabled
                const allOff = values.every(v => v === 2);  // All disabled
                const isMixed = !allOn && !allOff;          // Partial state

                return (
                    <div className="switch-center">
                        <Switch
                            size="large"
                            checked={allOn}
                            disabled={!isEditMode}
                            onChange={(checked) =>
                                handleAllToggleChange(checked, record)
                            }
                            className={`switch-toggle  ${isMixed ? 'switch-middle' : ''}`}
                        />
                    </div>
                );
            }
        },
        {
            title: 'Airbnb',
            dataIndex: 'is_airbnb_enable',
            key: 'is_airbnb_enable',
            width: '10%',
            render: (text, record) => (
                <div className="switch-center">
                    <Switch
                        onChange={(checked) =>
                            handleToggleChange(checked, record, 'is_airbnb_enable')
                        }
                        size="large"
                        checked={text === 1}
                        disabled={!isEditMode}
                        className='switch-toggle' />
                </div>
            )
        },
        {
            title: 'Vrbo',
            dataIndex: 'is_vrbo_enable',
            key: 'is_vrbo_enable',
            width: '10%',
            render: (text, record) => (
                <div className="switch-center">
                    <Switch
                        onChange={(checked) =>
                            handleToggleChange(checked, record, 'is_vrbo_enable')
                        }
                        checked={record.is_vrbo_enable === 1}
                        disabled={!isEditMode}
                        size="large" className='switch-toggle' />
                </div>
            )
        },
        {
            title: 'Others',
            dataIndex: 'is_other_enable',
            key: 'is_other_enable',
            width: '10%',
            render: (text, record) => (
                <div className="switch-center">
                    <Switch
                        onChange={(checked) =>
                            handleToggleChange(checked, record, 'is_other_enable')
                        }
                        size="large"
                        checked={record.is_other_enable === 1}
                        disabled={!isEditMode}
                        className='switch-toggle' />
                </div>
            )
        },
        listingLists?.length && {
            title: 'Listings',
            dataIndex: 'listings',
            key: 'listings',
            width: '33%',
            render: (_, record) => {
                const listingSlugs = record.listings ?? [];
                const total = record.listing?.total_listings ?? 0;
                const n = listingSlugs.length > 0 ? listingSlugs.length : total;
                return (
                    <div>
                        {isEditMode ? (
                            <a
                                className="common_urlLink font-14-700"
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    openListingSelectionPopup(record.feature_slug);
                                }}
                            >
                                Select Listings
                            </a>
                        ) : (
                            <>
                                <span className="font-14-700">
                                    {record.listing?.listing_name || ''}
                                </span>
                                {n > 1 && (
                                    <span className="font-14-700" style={{ whiteSpace: 'nowrap' }}>
                                        {' '}&amp;{' '}
                                        <a
                                            className="common_urlLink font-14-700"
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openListingSelectionPopup(record.feature_slug);
                                            }}
                                        >
                                            {`${n - 1} more`}
                                        </a>
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                );
            }
        },
    ].filter(Boolean);

    const handlePaginationChange = (page, pageSize) => {
        setPagination({
            ...pagination,
            currentPage: page,
            pageSize,
        });
    };
    const handlePageSizeChange = (current, size) => {
        setPagination(({
            ...pagination,
            currentPage: current,
            pageSize: size,
        }));
    }

    //  Fetch configuration list from API
    const fetchConfigurationdata = async () => {
        try {
            setSpinnerLoader(true);
            const parms = hostSlug ? '?host_slug=' + hostSlug : '';
            const response = await dispatch(
                getConfigurationDetails(parms, (response) => {
                    return response;
                }),
            );
            //  Handle API error
            if (response?.status !== HttpStatus.HTTP_OK) {
                Notification.addNotificationError(response.message);
            }
            //  Set data into state, keeping only the features currently used on this UI
            else {
                const allowed = ['veriff_verification', 'sex_offender'];
                const filteredList = (response?.result ?? []).filter(item => allowed.includes(item.feature_slug));
                setConfigurationList(filteredList);

                //  Initialise per-row listing selections from API data
                const initSelections = {};
                filteredList.forEach(item => {
                    initSelections[item.feature_slug] = Array.isArray(item.listings) ? item.listings : [];
                });
                setSelectedListings(initSelections);

                fetchHostListings();
            }
        } catch (error) {
        } finally {
            setSpinnerLoader(false);
        }
    };

    // Every listing selected for Offender Check must also be selected for ID Verification
    const offenderVeriffCheck = () => {
        if (!isFeatureChannelActive('sex_offender')) return true;

        const veriffListings = getEffectiveListings('veriff_verification');
        const offenderListings = getEffectiveListings('sex_offender');
        return offenderListings.every(slug => veriffListings.includes(slug));
    }

    // "All Listings" has no dedicated toggle in this UI — it is inferred when every
    // current listing is selected. Sending [] instead of every id lets the backend
    // keep treating it as "all listings", including ones created later.
    const isAllListingsSelected = (featureSlug) => {
        const selected = selectedListings[featureSlug] ?? [];
        return listingLists.length > 0 && selected.length === listingLists.length;
    };

    const handleFormSubmit = useCallback(
        async () => {
            //  Prepare payload
            if(!offenderVeriffCheck()){
                Notification.addNotificationError("Some listings selected for Offender Check are not selected for ID Verification. Please update the listings before saving.");
                return;
            }
            let requestData = {
                host_slug: hostSlug,
                configurations: configurationList.map((item) => ({
                    feature_slug: item.feature_slug,
                    is_airbnb_enable: item.is_airbnb_enable,
                    is_vrbo_enable: item.is_vrbo_enable,
                    is_booking_com_enable: 2,
                    is_other_enable: item.is_other_enable,
                    listings: isAllListingsSelected(item.feature_slug) ? '' : (selectedListings[item.feature_slug] ?? []).join(','),
                }))
            };

            //  Submit updated configuration to backend
            const updateConfigurations = async () => {
                try {
                    setSpinnerLoader(true);
                    const updateConfigurationResponse = await dispatch(
                        updateConfiguration(requestData, (response) => {
                            return response;
                        }),
                    );
                    if (updateConfigurationResponse?.status !== HttpStatus.HTTP_OK) {
                        Notification.addNotificationError(updateConfigurationResponse.message);
                    } else {
                        Notification.addNotificationSuccess(updateConfigurationResponse.message);
                        setIsEditMode(false);
                        fetchConfigurationdata();
                    }
                } catch (error) {
                } finally {
                    setSpinnerLoader(false);
                }
            }
            updateConfigurations();
        },
        [dispatch, configurationList, selectedListings],
    );

    useEffect(() => {
        if (isListingSelectionPopupOpen) {
            setPreListingData({
                selectedListing: structuredClone(selectedListings),
            });
        }
    }, [isListingSelectionPopupOpen]);

    useEffect(() => {
        fetchConfigurationdata();
    }, [hostSlug, pagination.currentPage, pagination.pageSize]);

    return (
        <>
            <Form form={form} name="Form" onFinish={handleFormSubmit} >
                <Spinner loader={spinnerLoader} />
                <Cards xs={24} headless className="borderRadius common_fiter_table">
                    <UserTableStyleWrapper>
                        <div className="contact-table">
                            <TableWrapper className="table-responsive">
                                <div className="configuration-table">
                                <Table
                                    dataSource={configurationList}
                                    columns={cmsTableColumns}
                                    rowKey="slug"
                                    pagination={{
                                        current: pagination.currentPage,
                                        pageSize: pagination.pageSize,
                                        total: pagination.totalItems,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                        onChange: handlePaginationChange,
                                        onShowSizeChange: handlePageSizeChange
                                    }}
                                />
                                </div>
                            </TableWrapper>
                        </div>
                    </UserTableStyleWrapper>
                </Cards>
            </Form>
            <div className="manage-listing-btn">
                {!isEditMode ? (
                    <Button
                        outlined
                        shape="circle"
                        type="primary"
                        className="w-100 edit-btn"
                        onClick={handleEnterEditMode}
                    >
                        Edit
                    </Button>
                ) : (
                    <>
                        <Button
                            htmlType="submit"
                            size="default"
                            shape="circle"
                            type="primary"
                            className="update-btn"
                            onClick={() => form.submit()}
                            disabled={spinnerLoader}
                        >
                            {spinnerLoader ? 'Loading...' : 'Save'}
                        </Button>
                        <Button
                            onClick={handleCancelEdit}
                            shape="circle"
                            className="form-cancel-btn"
                            outlined
                            type="primary"
                            disabled={spinnerLoader}
                        >
                            Cancel
                        </Button>
                    </>
                )}
            </div>

            {/* Listing Selection Modal */}
            <Modal
                type="primary"
                open={isListingSelectionPopupOpen}
                footer={null}
                onCancel={() => closeListingSelectionPopup(true)}
                width={'90%'}
                maskClosable={false}
                aria-hidden="false"
                className="bulk-listing-modal template-config-listing-modal"
                title={`Selected Listings (${selectedListings[currentFeatureSlug]?.length ?? 0}/${listingLists?.length})`}
            >
                <div className="guide-matching-listing">
                    <div className="project-modal">
                        <div className="guide-card">
                            <div className="guide-card-body">
                                <UserTableStyleWrapper>
                                    <div className="contact-table">
                                        <TableWrapper className="table-responsive">
                                            <Table
                                                dataSource={listingTableData}
                                                columns={listingTableColumns}
                                                rowKey="key"
                                                pagination={false}
                                                className="listing-no-thead-table"
                                            />
                                        </TableWrapper>
                                    </div>
                                </UserTableStyleWrapper>
                            </div>
                            {isEditMode && (
                                <div className="bulk-editbtn-box">
                                    <Button
                                        htmlType="submit"
                                        size="default"
                                        type="dark"
                                        shape="circle"
                                        key="submit"
                                        onClick={() => handleListingSelection()}
                                    >
                                        Update
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                open={isInfoModalOpen}
                onCancel={() => setIsInfoModalOpen(false)}
                footer={null}
                className="modal-md"
                destroyOnClose
            >
                <div className="Idmodalbox earlyCheckinPopup">
                    <div className="hostPayout">
                        <div
                            className="modal-content"
                            dangerouslySetInnerHTML={{ __html: selectedDescription }}
                        />
                    </div>
                </div>
            </Modal>
        </>
    )

}
export default Configurations